import React, { useEffect, useState, useRef, useCallback } from "react";
import { api, openWS, translateMessages, fmtTime, mediaSrcById } from "../api";
import { subscribeQueue, retryQueued, removeQueued } from "../lib/offlineQueue";
import { clearConversationNotifications } from "../lib/push";
import { useApp } from "../context/AppContext";
import { MessageBubble } from "./MessageBubble";
import { MediaGroupBubble } from "./MediaGroupBubble";
import { MirroredBubble } from "./MirroredBubble";
import { Composer } from "./Composer";
import { PhotoViewer } from "./PhotoViewer";
import { VideoViewer } from "./VideoViewer";
import { IssueDialog, TaskFromMessageDialog, JobPickerDialog, MeasurementDialog } from "./StructuredDialogs";
import { CatalogEditPicker } from "./CatalogEditPicker";
import { ResolveIssueDialog } from "./ResolveIssueDialog";
import { CatchUpDialog } from "./CatchUpDialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { Search, Briefcase, X, Hammer, ChevronRight, Link2, Unlink, Clock, RefreshCw, Trash2, CloudOff, ChevronDown, ChevronUp, Star, Pin, FileText, Camera, Video as VideoIcon, Mic, Sparkles } from "lucide-react";
import { useTypingPresence } from "../lib/typing";
import { Capacitor } from "@capacitor/core";
import { micExclusiveAcquire, micExclusiveRelease } from "../lib/wakeword";
import { offlineEnabled, readMessages, mirrorMessages, forgetMessage, patchMirrored, searchMirror } from "../lib/offlineSync";

const DAY_LOCALES = { pt: "pt-BR", es: "es-ES", en: "en-US" };
function dayLabel(iso, lang, t) {
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (diffDays === 0) return t("today");
  if (diffDays === 1) return t("yesterday");
  const loc = DAY_LOCALES[lang] || "en-US";
  if (diffDays < 7) return d.toLocaleDateString(loc, { weekday: "long" });
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(loc, sameYear ? { day: "numeric", month: "long" } : { day: "numeric", month: "short", year: "numeric" });
}

export function Thread({ conversation, contextJob, focusMessageId }) {
  const { user, t, lang } = useApp();
  // Phone: the always-on "Hey Buddy" recognizer keeps a record session open, which mutes
  // haptics and lags typing — hold the mic lock while a conversation is open, release on exit.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    micExclusiveAcquire();
    return () => micExclusiveRelease();
  }, []);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  // Reply draft persistence — the quoted message survives navigation/app close
  // (restored together with the text draft kept by the Composer)
  const [replyContext, setReplyContext] = useState(() => {
    try {
      const raw = localStorage.getItem(`osc_draft_reply_${conversation.id}`);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  });
  const [viewer, setViewer] = useState(null);
  const [videoViewer, setVideoViewer] = useState(null); // {media, message}
  const [reads, setReads] = useState(conversation.reads || {});
  const [delivers, setDelivers] = useState(conversation.delivers || {});
  // Job topic (persistent link) state — pre-activated when opened from inside a Job Hub
  // The topic is PERSISTED on the conversation (server) and shared by both
  // sides: every message from anyone is linked until any participant ends it.
  const topicJob = (tp) => (tp && tp.job_id ? { id: tp.job_id, job_number: tp.job_number, customer_name: tp.customer_name } : null);
  const canTopic = conversation.type !== "job" && !conversation.job_id;
  const [linkedJob, setLinkedJobState] = useState(topicJob(conversation.active_topic) || contextJob || null);
  const [linkVisibility, setLinkVisibilityState] = useState(
    conversation.active_topic?.visibility || (contextJob ? "shared" : "private"));
  const setLinkedJob = (job, vis) => {
    setLinkedJobState(job);
    if (!canTopic) return;
    api.post(`/conversations/${conversation.id}/topic`, {
      user_id: user.id, job_id: job?.id || null, visibility: vis || linkVisibility,
    }).catch(() => {});
  };
  const setLinkVisibility = (v) => {
    setLinkVisibilityState(v);
    if (canTopic && linkedJob) {
      api.post(`/conversations/${conversation.id}/topic`, { user_id: user.id, job_id: linkedJob.id, visibility: v }).catch(() => {});
    }
  };
  const [suggestDismissed, setSuggestDismissed] = useState(false);
  // Selection mode (link multiple messages to a job)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  // Search & job filter — job context opens the conversation as a filtered view
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // WhatsApp-style in-conversation search: matches across the FULL history
  // (backend), navigated with ▲▼ and highlighted in yellow inside bubbles.
  const [searchMatches, setSearchMatches] = useState([]); // [{id, created_at}] asc
  const [matchIdx, setMatchIdx] = useState(-1);
  const [jobFilter, setJobFilter] = useState(contextJob || null);
  const [sharedJobsOpen, setSharedJobsOpen] = useState(false);
  const [sharedJobs, setSharedJobs] = useState([]);
  // Create issue/task from message
  const [picker, setPicker] = useState(null); // {mode:'selection'|'issue'|'task', message?}
  const [issueFrom, setIssueFrom] = useState(null); // {message, jobId}
  const [taskFrom, setTaskFrom] = useState(null); // {message, jobId}
  const [resolveFor, setResolveFor] = useState(null); // issue message being resolved
  // Offline queue: pending items for this conversation
  const [queuedItems, setQueuedItems] = useState([]);
  const presence = useTypingPresence(conversation.id, user.id);
  useEffect(() => { if (presence.length) scrollToBottom(true); }, [presence.length]); // eslint-disable-line react-hooks/exhaustive-deps
  // "O que perdi?" — AI catch-up: capture the last-read watermark BEFORE
  // markRead() overwrites it, so the summary covers what was truly unread.
  const catchupSinceRef = useRef((conversation.reads || {})[user.id] || null);
  // WhatsApp-style "N unread messages" divider: fixed when the thread opens, stays until reopened
  const unreadMarkerRef = useRef(null); // { id, count }
  const [missedCount, setMissedCount] = useState(0);
  const [catchupOpen, setCatchupOpen] = useState(false);
  const [catchupDismissed, setCatchupDismissed] = useState(false);
  const scrollRef = useRef(null);
  const usersMapRef = useRef({});
  const translationReqRef = useRef({}); // {lang: Set(message ids already requested)}
  const autoScrollUntilRef = useRef(0); // keep snapping to bottom while media loads right after opening
  const stickBottomRef = useRef(true); // true while the user is at (or near) the bottom
  const focusUntilRef = useRef(0); // deep-link focus window (keep re-centering while media loads)
  const [focusedId, setFocusedId] = useState(null);
  const [starredIds, setStarredIds] = useState(new Set());
  const [starredOpen, setStarredOpen] = useState(false);
  const [starredList, setStarredList] = useState(null);
  const [pins, setPins] = useState([]);
  const [pinTarget, setPinTarget] = useState(null); // message awaiting pin-duration choice
  const [showJump, setShowJump] = useState(false); // floating jump-to-latest button
  const [newCount, setNewCount] = useState(0); // messages that arrived while scrolled up ("↓ 3 new")

  (conversation.participants || []).forEach((p) => { usersMapRef.current[p.id] = p; });

  // ---- "↓ N new" — messages from others that appended while scrolled up ----
  const seenIdsRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    if (!seenIdsRef.current) { seenIdsRef.current = new Set(messages.map((m) => m.id)); return; }
    const seen = seenIdsRef.current;
    let fresh = 0;
    for (const m of messages) {
      if (!seen.has(m.id)) { seen.add(m.id); if (m.sender_id !== user.id) fresh += 1; }
    }
    if (fresh > 0 && !stickBottomRef.current) setNewCount((c) => c + fresh);
  }, [messages, loading, user.id]);

  // ---- Automatic translation into the viewer's language ----
  useEffect(() => {
    if (!messages.length) return;
    if (!translationReqRef.current[lang]) translationReqRef.current[lang] = new Set();
    const requested = translationReqRef.current[lang];
    // same language on both sides (sender's profile language = mine) → never auto-translate
    const need = messages.filter((m) =>
      ((m.content && m.content.trim()) || (m.meta?.transcript && m.meta.transcript.trim())) &&
      (usersMapRef.current[m.sender_id]?.language || "") !== lang &&
      !(m.translations && m.translations[lang]) &&
      !requested.has(m.id));
    if (need.length === 0) return;
    need.forEach((m) => requested.add(m.id));
    translateMessages(need.map((m) => m.id), lang)
      .then((map) => {
        if (!map || Object.keys(map).length === 0) return;
        setMessages((prev) => prev.map((m) => (
          map[m.id] ? { ...m, translations: { ...(m.translations || {}), [lang]: map[m.id] } } : m
        )));
      })
      .catch(() => {});
  }, [messages, lang]);

  // ---- Starred (favorites) + Pins ----
  const refreshPins = useCallback(async () => {
    try {
      const r = await api.get(`/conversations/${conversation.id}/pins`);
      setPins(r.data || []);
    } catch (e) { /* ignore */ }
  }, [conversation.id]);

  useEffect(() => {
    let alive = true;
    api.get(`/conversations/${conversation.id}/starred?user_id=${user.id}`)
      .then((r) => { if (alive) setStarredIds(new Set((r.data || []).map((m) => m.id))); })
      .catch(() => {});
    refreshPins();
    return () => { alive = false; };
  }, [conversation.id, user.id, refreshPins]);

  const toggleStar = async (message) => {
    const has = starredIds.has(message.id);
    try {
      if (has) await api.delete(`/messages/${message.id}/star?user_id=${user.id}`);
      else await api.post(`/messages/${message.id}/star`, { user_id: user.id });
      setStarredIds((prev) => {
        const n = new Set(prev);
        if (has) n.delete(message.id); else n.add(message.id);
        return n;
      });
      toast.success(has ? t("removed_favorite") : t("added_favorite"));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  const pinFor = async (hours) => {
    const msg = pinTarget;
    setPinTarget(null);
    if (!msg) return;
    try {
      await api.post(`/messages/${msg.id}/pin`, { user_id: user.id, hours });
      toast.success(t("message_pinned"));
      refreshPins();
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  const unpin = async (pin) => {
    try {
      await api.delete(`/pins/${pin.id}?user_id=${user.id}`);
      refreshPins();
    } catch (e) {
      toast.error(e?.response?.status === 403 ? t("something_wrong") : t("something_wrong"));
    }
  };

  const canUnpin = (p) => p.pinned_by === user.id || ["supervisor", "admin", "manager", "office"].includes(user.role);

  const openStarred = async () => {
    setStarredOpen(true);
    setStarredList(null);
    try {
      const r = await api.get(`/conversations/${conversation.id}/starred?user_id=${user.id}`);
      setStarredList(r.data || []);
    } catch (e) {
      setStarredList([]);
    }
  };

  const msgPreview = (msg) => {
    // Polls must be recognizable when pinned/starred — not look like a plain message
    if (msg.type === "poll") return `📊 ${t("poll")}: ${msg.content || ""}`.replace(/:\s*$/, "").trim();
    if (msg.content) return msg.content;
    const fn = (msg.media_filenames || [])[0] || (msg.media || [])[0]?.filename;
    const map = { photo: "📷", video: "🎬", document: "📄", voice: "🎤" };
    return `${map[msg.type] || ""} ${fn || t(msg.type) || msg.type}`.trim();
  };

  const scrollToBottom = useCallback((smooth = false) => {
    requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
    });
  }, []);

  // Keyboard opens (native resize) / viewport shrinks: if we were at the bottom,
  // stay at the bottom so the last message remains visible above the keyboard
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    let lastH = el.clientHeight;
    const keep = () => {
      const h = el.clientHeight;
      const shrank = h < lastH;
      lastH = h;
      if (shrank && stickBottomRef.current) {
        scrollToBottom();
        setTimeout(() => scrollToBottom(), 120);
      }
    };
    const ro = new ResizeObserver(keep);
    ro.observe(el);
    const onKb = () => { if (stickBottomRef.current) { scrollToBottom(); setTimeout(() => scrollToBottom(), 150); setTimeout(() => scrollToBottom(), 400); } };
    window.addEventListener("keyboardWillShow", onKb);
    window.addEventListener("keyboardDidShow", onKb);
    window.visualViewport?.addEventListener("resize", keep);
    return () => {
      ro.disconnect();
      window.removeEventListener("keyboardWillShow", onKb);
      window.removeEventListener("keyboardDidShow", onKb);
      window.visualViewport?.removeEventListener("resize", keep);
    };
  }, [scrollToBottom]);


  const markRead = useCallback(() => {
    api.post(`/conversations/${conversation.id}/read`, { user_id: user.id }).catch(() => {});
    // Opening the thread also clears this conversation's notifications from
    // the phone's notification center (other conversations stay untouched)
    clearConversationNotifications(conversation.id);
  }, [conversation.id, user.id]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    // Offline mirror first (instant, works without signal), then the server
    let gotServer = false;
    const cached = offlineEnabled(user.id) ? readMessages(conversation.id) : Promise.resolve([]);
    cached.then((list) => { if (mounted && list.length && !gotServer) { setMessages(list); setLoading(false); } });
    api.get(`/conversations/${conversation.id}/messages`).then((res) => {
      if (!mounted) return;
      gotServer = true;
      setMessages(res.data);
      setLoading(false);
      mirrorMessages(user.id, res.data);
      // Count what was missed while away (before markRead clears the watermark)
      const since = catchupSinceRef.current;
      let firstUnread = null;
      if (since) {
        const missedList = res.data.filter(
          (m) => m.created_at > since && m.sender_id !== user.id && !m.is_deleted
        );
        setMissedCount(missedList.length);
        if (missedList.length > 0) {
          firstUnread = missedList[0];
          unreadMarkerRef.current = { id: firstUnread.id, count: missedList.length };
        }
      }
      markRead();
      if (!focusMessageId && firstUnread) {
        // Open at the "unread" divider (like WhatsApp) instead of the very bottom
        autoScrollUntilRef.current = 0;
        stickBottomRef.current = false;
        const tryScroll = (attempt = 0) => {
          const el = document.getElementById("unread-divider");
          if (el) el.scrollIntoView({ block: "start" });
          else if (attempt < 15) setTimeout(() => tryScroll(attempt + 1), 150);
          else { stickBottomRef.current = true; scrollToBottom(); }
        };
        requestAnimationFrame(() => tryScroll());
      } else if (focusMessageId) {
        // Deep link (e.g. from an Issue/Task card): scroll to the source message ONCE and
        // flash a short highlight (0.5s). Never fight the user's scroll afterwards.
        autoScrollUntilRef.current = 0;
        stickBottomRef.current = false;
        // Tiny settle window so images above can shift layout right after render;
        // cancelled instantly on any user touch/wheel (see effect below).
        focusUntilRef.current = Date.now() + 600;
        setFocusedId(focusMessageId);
        const centerTarget = () => {
          const el = document.getElementById(`msg-${focusMessageId}`);
          if (el) el.scrollIntoView({ block: "center" });
          return !!el;
        };
        const tryFocus = (attempt = 0) => {
          if (centerTarget()) {
            // Scroll happened exactly once; clear the highlight after 0.5s
            setTimeout(() => setFocusedId(null), 500);
            setTimeout(() => { focusUntilRef.current = 0; }, 600);
          } else if (attempt < 15) {
            setTimeout(() => tryFocus(attempt + 1), 200);
          } else {
            stickBottomRef.current = true;
            scrollToBottom();
            setFocusedId(null);
          }
        };
        requestAnimationFrame(() => tryFocus());
      } else {
        // Always open at the bottom (latest messages), re-snapping while photos load
        stickBottomRef.current = true;
        autoScrollUntilRef.current = Date.now() + 3000;
        scrollToBottom();
        [120, 350, 700, 1200, 2000].forEach((d) => setTimeout(() => {
          if (Date.now() < autoScrollUntilRef.current) scrollToBottom();
        }, d));
      }
    }).catch(() => {
      // no signal: whatever the mirror has is what we show
      cached.then((list) => { if (!mounted) return; if (list.length) setMessages(list); setLoading(false); });
    });

    // Refill the thread after a WebSocket reconnect — everything broadcast
    // while the socket was down (common after the app returns from the
    // background on mobile) would otherwise be lost until reopening the chat.
    const refetchSilent = () => {
      api.get(`/conversations/${conversation.id}/messages`).then((res) => {
        if (!mounted) return;
        setMessages(res.data);
        mirrorMessages(user.id, res.data);
        markRead();
      }).catch(() => {});
    };

    const close = openWS(`conv_${conversation.id}`, (payload) => {
      if (payload.event === "typing" && payload.data) {
        try { window.dispatchEvent(new CustomEvent("osc-conv-typing", { detail: { conversation_id: conversation.id, ...payload.data } })); } catch (e) { /* ignore */ }
        return;
      }
      if (payload.event === "message" && payload.data) mirrorMessages(user.id, [payload.data]);
      if (payload.event === "message_update" && payload.data?.id) patchMirrored(user.id, payload.data);
      if (payload.event === "message_delete" && payload.data?.id) forgetMessage(user.id, payload.data.id);
      if (payload.event === "topic_update" && payload.data) {
        // the other side started / ended the job topic
        const tp = payload.data.active_topic;
        setLinkedJobState(topicJob(tp));
        if (tp?.visibility) setLinkVisibilityState(tp.visibility);
        return;
      }
      if (payload.event === "message" && payload.data) {
        setMessages((prev) => {
          if (prev.find((m) => m.id === payload.data.id)) return prev;
          return [...prev, payload.data];
        });
        // Scrolled up reading older messages? Don't yank the view — the
        // "↓ N new" pill (effect on `messages`) takes over instead
        if (payload.data.sender_id === user.id || stickBottomRef.current) scrollToBottom(true);
        markRead();
      } else if (payload.event === "message_update" && payload.data) {
        setMessages((prev) => prev.map((m) => (m.id === payload.data.id ? { ...m, ...payload.data } : m)));
      } else if (payload.event === "played" && payload.data) {
        // WhatsApp blue mic: a recipient played my voice note
        setMessages((prev) => prev.map((m) => (m.id === payload.data.message_id
          ? { ...m, played_by: { ...(m.played_by || {}), [payload.data.user_id]: payload.data.at } } : m)));
      } else if (payload.event === "read" && payload.data) {
        setReads((prev) => ({ ...prev, [payload.data.user_id]: payload.data.at }));
        // reading implies delivery — keep both watermarks in sync live
        setDelivers((prev) => ({ ...prev, [payload.data.user_id]: payload.data.at }));
      } else if (payload.event === "delivered" && payload.data) {
        setDelivers((prev) => ({ ...prev, [payload.data.user_id]: payload.data.at }));
      } else if (payload.event === "issue_update" && payload.data) {
        setMessages((prev) => prev.map((m) => {
          if (m.meta?.issue_id !== payload.data.id) return m;
          const patch = { status: payload.data.status };
          if (payload.data.title) patch.title = payload.data.title;
          if (payload.data.location) patch.location = payload.data.location;
          if (payload.data.priority) patch.priority = payload.data.priority;
          return { ...m, meta: { ...m.meta, ...patch } };
        }));
      } else if (payload.event === "task_update" && payload.data) {
        setMessages((prev) => prev.map((m) => {
          if (m.meta?.task_id !== payload.data.id || m.id !== payload.data.message_id) return m;
          const next = { ...m, meta: { ...m.meta } };
          if (payload.data.status) next.meta.status = payload.data.status;
          if (payload.data.due_label != null) next.meta.due_label = payload.data.due_label;
          if (payload.data.title) next.content = payload.data.title;
          return next;
        }));
      } else if (payload.event === "message_delete" && payload.data) {
        setMessages((prev) => prev.filter((m) => m.id !== payload.data.id));
      } else if (payload.event === "pins_update") {
        refreshPins();
      }
    }, refetchSilent);
    return () => { mounted = false; close(); };
  }, [conversation.id, scrollToBottom, markRead, focusMessageId, refreshPins]);

  // App resumed / tab became visible: silently re-sync the thread
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      api.get(`/conversations/${conversation.id}/messages`)
        .then((res) => setMessages(res.data))
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [conversation.id]);

  // Keep the thread anchored: track whether the user is near the bottom, and
  // re-snap when images/media finish loading (they change the scroll height)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      stickBottomRef.current = dist < 150;
      setShowJump(dist > 400);
      if (dist < 150) setNewCount(0);
    };
    const onMediaLoad = () => {
      if (Date.now() < focusUntilRef.current && focusMessageId) {
        const target = document.getElementById(`msg-${focusMessageId}`);
        if (target) target.scrollIntoView({ block: "center" });
        return;
      }
      if (stickBottomRef.current || Date.now() < autoScrollUntilRef.current) scrollToBottom();
    };
    // Any deliberate user gesture cancels auto-centering immediately (no scroll hijacking)
    const cancelAutoCenter = () => {
      focusUntilRef.current = 0;
      autoScrollUntilRef.current = 0;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("load", onMediaLoad, true);
    el.addEventListener("wheel", cancelAutoCenter, { passive: true });
    el.addEventListener("touchstart", cancelAutoCenter, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("load", onMediaLoad, true);
      el.removeEventListener("wheel", cancelAutoCenter);
      el.removeEventListener("touchstart", cancelAutoCenter);
    };
  }, [scrollToBottom, focusMessageId]);

  const toggleDecision = async (message) => {
    try {
      await api.patch(`/messages/${message.id}/decision`, { is_decision: !message.is_decision, user_id: user.id });
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, is_decision: !message.is_decision } : m)));
      toast.success(!message.is_decision ? t("decision") : "\u2713");
    } catch (e) {
      toast.error("Failed");
    }
  };

  const reactToMessage = async (message, emoji) => {
    try {
      await api.post(`/messages/${message.id}/react`, { user_id: user.id, emoji });
    } catch (e) {
      toast.error("Failed");
    }
  };

  // WhatsApp-style deletion: anyone can delete FOR ME (any message);
  // "delete for everyone" = own recent messages (48h) or admin anytime.
  const [deleteMsg, setDeleteMsg] = useState(null);
  const canDeleteForAll = (m) =>
    m && (user.role === "admin" ||
      (m.sender_id === user.id &&
       Date.now() - new Date(m.created_at).getTime() < 48 * 60 * 60 * 1000));
  const confirmDeleteMessage = async () => {
    if (!deleteMsg) return;
    const id = deleteMsg.id;
    setDeleteMsg(null);
    try {
      await api.delete(`/messages/${id}`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success(t("message_deleted"));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      toast.error(detail === "delete_window_expired" ? t("delete_window_expired") : (detail || "Failed"));
    }
  };
  const hideMessageForMe = async () => {
    if (!deleteMsg) return;
    const id = deleteMsg.id;
    setDeleteMsg(null);
    try {
      await api.post(`/messages/${id}/hide`);
      setMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success(t("message_deleted"));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  const changeIssueStatus = async (issueId, status) => {
    try {
      await api.patch(`/issues/${issueId}`, { status, user_id: user.id });
      toast.success(t(`status_${status}`));
    } catch (e) {
      toast.error("Failed");
    }
  };

  // ---- Selection / bulk link ----
  const startSelect = (message) => {
    setSelectMode(true);
    setSelectedIds([message.id]);
  };
  const toggleSelect = (message) => {
    setSelectedIds((prev) => (prev.includes(message.id) ? prev.filter((i) => i !== message.id) : [...prev, message.id]));
  };
  const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };

  const linkSelectedTo = async (job) => {
    try {
      await api.patch("/messages/link", { message_ids: selectedIds, job_id: job.id, user_id: user.id, visibility: "private" });
      toast.success(`${selectedIds.length} \u2192 #${job.job_number}`);
    } catch (e) {
      toast.error("Failed");
    }
    exitSelect();
    setPicker(null);
  };

  const unlinkMessages = async (ids) => {
    try {
      const { data } = await api.patch("/messages/unlink", { message_ids: ids, user_id: user.id });
      toast.success(`${data.updated} ${t("unlinked_from_job")}`);
    } catch (e) {
      toast.error("Failed");
    }
  };
  const selectedLinked = selectedIds.filter((id) => messages.find((m) => m.id === id)?.meta?.linked_job_id);

  const shareWithJob = async (message) => {
    try {
      await api.patch("/messages/link", { message_ids: [message.id], job_id: message.meta.linked_job_id, user_id: user.id, visibility: "shared" });
      toast.success(t("shared"));
    } catch (e) {
      toast.error("Failed");
    }
  };

  // ---- Create issue/task from a message ----
  const createIssueFrom = (message) => {
    const jid = message.meta?.linked_job_id || conversation.job_id;
    if (jid) setIssueFrom({ message, jobId: jid });
    else setPicker({ mode: "issue", message });
  };
  const createTaskFrom = (message) => {
    const jid = message.meta?.linked_job_id || conversation.job_id;
    if (jid) setTaskFrom({ message, jobId: jid });
    else setPicker({ mode: "task", message });
  };

  // Link the source message (e.g. a photo) to the picked job so the job badge shows on it
  const linkSourceMessage = (message, job) => {
    if (!message || message.meta?.linked_job_id) return;
    api.patch("/messages/link", {
      message_ids: [message.id], job_id: job.id, user_id: user.id, visibility: "private",
    }).catch(() => {});
  };

  const onPickJob = (job) => {
    if (!picker) return;
    if (picker.mode === "selection") return linkSelectedTo(job);
    if (picker.mode === "savefile") {
      saveToJobFiles(picker.message, job);
      setPicker(null);
      return;
    }
    if (picker.mode === "issue") {
      linkSourceMessage(picker.message, job);
      setIssueFrom({ message: picker.message, jobId: job.id });
    }
    if (picker.mode === "task") {
      linkSourceMessage(picker.message, job);
      setTaskFrom({ message: picker.message, jobId: job.id });
    }
    setPicker(null);
  };

  // ---- Save a PDF/document into the job's Files tab ----
  const saveToJobFiles = async (message, job) => {
    const mediaId = (message.media_ids || [])[0];
    if (!mediaId) return;
    try {
      const res = await api.post(`/media/${mediaId}/assign-job`, { job_id: job.id, user_id: user.id });
      toast.success(`${t("saved_to_files")} #${res.data.job_number || ""}`);
    } catch (e) {
      toast.error("Failed");
    }
  };
  const onSaveToFiles = (message) => {
    const jid = message.meta?.linked_job_id || conversation.job_id;
    if (jid) saveToJobFiles(message, { id: jid });
    else setPicker({ mode: "savefile", message });
  };

  // ---- Reply to any message (quote + quick shortcut back to the original) ----
  // Persist the reply draft; drop oversized crop previews (the bar falls back
  // to the original photo via quote.media_id)
  useEffect(() => {
    const key = `osc_draft_reply_${conversation.id}`;
    try {
      if (replyContext) {
        const { cropDataUrl, ...rest } = replyContext;
        const toSave = cropDataUrl && cropDataUrl.length < 300000 ? replyContext : rest;
        localStorage.setItem(key, JSON.stringify(toSave));
      } else {
        localStorage.removeItem(key);
      }
    } catch (e) { /* storage unavailable — best effort */ }
  }, [replyContext, conversation.id]);

  const replyToMessage = (message) => {
    const senderName = usersMapRef.current[message.sender_id]?.name || "";
    const preview = message.type === "voice"
      ? (message.meta?.transcript || t("voice_message"))
      : (message.meta?.title || message.content || "");
    setReplyContext({
      quote: {
        message_id: message.id,
        sender_id: message.sender_id,
        sender_name: senderName,
        content: (preview || "").slice(0, 140),
        type: message.type,
        media_id: (message.media_ids || [])[0] || null,
      },
    });
  };

  // WhatsApp-style edit: opens the composer pre-filled with the message text
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingMeasure, setEditingMeasure] = useState(null); // measurement re-edit (≤ 2h)
  const [catalogPick, setCatalogPick] = useState(null); // catalog message → "which opening?"

  // Jump to (and highlight) a message already loaded in this thread
  const jumpToMessage = (mid) => {
    const el = document.getElementById(`msg-${mid}`);
    if (!el) return;
    stickBottomRef.current = false;
    setFocusedId(mid);
    el.scrollIntoView({ block: "center", behavior: "smooth" });
    setTimeout(() => setFocusedId(null), 500);
  };

  const openSharedJobs = async () => {
    const res = await api.get(`/conversations/${conversation.id}/shared-jobs`);
    setSharedJobs(res.data);
    setSharedJobsOpen(true);
  };

  // ---- In-conversation search (WhatsApp style) ----
  const searchActive = searchOpen && searchQuery.trim().length >= 2;

  // Jump to a search match: scroll+flash if loaded; otherwise fetch the
  // history block from the match forward, merge it in, then jump.
  const goToMatch = useCallback((list, idx) => {
    const m = list[idx];
    if (!m) return;
    stickBottomRef.current = false;
    autoScrollUntilRef.current = 0;
    const tryJump = () => {
      const el = document.getElementById(`msg-${m.id}`);
      if (!el) return false;
      setFocusedId(m.id);
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      setTimeout(() => setFocusedId((cur) => (cur === m.id ? null : cur)), 1200);
      return true;
    };
    if (tryJump()) return;
    // Match is older than what's loaded — pull history from that point forward
    api.get(`/conversations/${conversation.id}/messages`, { params: { after: m.created_at, limit: 2000 } })
      .then((res) => {
        setMessages((prev) => {
          const seen = new Set(prev.map((x) => x.id));
          const merged = [...prev];
          (res.data || []).forEach((x) => { if (!seen.has(x.id)) merged.push(x); });
          merged.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
          return merged;
        });
        let attempts = 0;
        const retry = () => {
          if (tryJump() || attempts++ > 12) return;
          setTimeout(retry, 150);
        };
        requestAnimationFrame(retry);
      })
      .catch(() => {});
  }, [conversation.id]);

  const navMatch = (dir) => {
    if (!searchMatches.length) return;
    const next = Math.min(Math.max(matchIdx + dir, 0), searchMatches.length - 1);
    if (next === matchIdx) return;
    setMatchIdx(next);
    goToMatch(searchMatches, next);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery("");
    setSearchMatches([]);
    setMatchIdx(-1);
  };

  // Debounced full-history search against the backend
  useEffect(() => {
    if (!searchOpen) return undefined;
    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchMatches([]);
      setMatchIdx(-1);
      return undefined;
    }
    const timer = setTimeout(() => {
      api.get(`/conversations/${conversation.id}/messages/search`, { params: { q, lang } })
        .catch(async (err) => {
          // no signal → search the copy kept on this phone
          if (offlineEnabled(user.id)) return { data: await searchMirror(q, conversation.id) };
          throw err;
        })
        .then((r) => {
          const list = r.data || [];
          setSearchMatches(list);
          if (list.length > 0) {
            const idx = list.length - 1; // start at the most recent match
            setMatchIdx(idx);
            goToMatch(list, idx);
          } else {
            setMatchIdx(-1);
          }
        })
        .catch(() => {});
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, searchOpen, conversation.id, lang]);

  // Mobile: tapping the conversation area (outside the keyboard/composer)
  // dismisses the keyboard — WhatsApp behavior. Touch devices only.
  const dismissKeyboardOnTap = () => {
    if (!window.matchMedia || !window.matchMedia("(pointer: coarse)").matches) return;
    const ae = document.activeElement;
    if (ae && (ae.tagName === "TEXTAREA" || (ae.tagName === "INPUT" && ae.type !== "file"))) {
      ae.blur();
      // native: make sure the keyboard really goes away (WhatsApp-style tap-to-dismiss)
      import("@capacitor/keyboard").then(({ Keyboard }) => Keyboard.hide().catch(() => {})).catch(() => {});
    }
  };

  // ---- Filtering ----
  const displayed = messages.filter((m) => {
    if (jobFilter && m.meta?.linked_job_id !== jobFilter.id) return false;
    return true;
  });

  // Group 3+ consecutive photo/video messages from the same sender into a collage
  // (photo bursts, video-markup sequences) for a cleaner conversation.
  const renderItems = (() => {
    if (selectMode) return displayed.filter((m) => !m.mirrored).map((m) => ({ kind: "msg", id: m.id, message: m }));
    const items = [];
    let buf = [];
    const flush = () => {
      if (buf.length >= 3) items.push({ kind: "group", id: `grp-${buf[0].id}`, messages: buf });
      else buf.forEach((b) => items.push({ kind: "msg", id: b.id, message: b }));
      buf = [];
    };
    for (const m of displayed) {
      if (m.mirrored) {
        flush();
        items.push({ kind: "mirror", id: `mir-${m.id}`, message: m });
        continue;
      }
      const groupable = !searchActive && (m.type === "photo" || m.type === "video") && (m.media?.length > 0) && !m.meta?.issue_id && !m.is_decision;
      if (groupable) {
        if (buf.length > 0 && buf[buf.length - 1].sender_id !== m.sender_id) flush();
        buf.push(m);
      } else {
        flush();
        items.push({ kind: "msg", id: m.id, message: m });
      }
    }
    flush();
    if (searchActive || selectMode) return items;
    // Day separators ("Today", "Yesterday", weekday, date) + the unread divider
    const itemDate = (it) => (it.kind === "group" ? it.messages[0] : it.message)?.created_at;
    const out = [];
    let lastDay = "";
    const marker = unreadMarkerRef.current;
    for (const it of items) {
      const iso = itemDate(it);
      const day = iso ? new Date(iso).toDateString() : "";
      if (day && day !== lastDay) {
        out.push({ kind: "day", id: `day-${day}`, label: dayLabel(iso, lang, t) });
        lastDay = day;
      }
      const firstId = it.kind === "group" ? it.messages[0].id : it.message.id;
      if (marker && (firstId === marker.id || (it.kind === "group" && it.messages.some((m) => m.id === marker.id)))) {
        out.push({ kind: "unread", id: "unread-divider", count: marker.count });
      }
      out.push(it);
    }
    return out;
  })();

  // Split the flat list into per-day groups (each starts with its "day" separator)
  const dayGroups = (() => {
    const groups = [];
    let cur = null;
    for (const it of renderItems) {
      if (it.kind === "day" || !cur) { cur = { key: it.kind === "day" ? it.id : `g-${groups.length}`, items: [] }; groups.push(cur); }
      cur.items.push(it);
    }
    return groups;
  })();


  const suggested = conversation.suggested_job;
  const showContextBar = conversation.type === "dm" && suggested && !linkedJob && !suggestDismissed;

  // Subscribe to the offline queue for this conversation
  useEffect(() => {
    const unsub = subscribeQueue((q) => {
      setQueuedItems(q.filter((i) => i.conversation_id === conversation.id));
    });
    // When a queued item is delivered, show it right away
    // (WS may still be reconnecting after the connection returns)
    const onQueuedSent = (e) => {
      const msg = e.detail;
      if (!msg || msg.conversation_id !== conversation.id) return;
      setMessages((prev) => {
        if (prev.find((m) => m.id === msg.id)) return prev;
        return [...prev, msg];
      });
      scrollToBottom(true);
    };
    window.addEventListener("osc-queued-sent", onQueuedSent);
    return () => {
      unsub();
      window.removeEventListener("osc-queued-sent", onQueuedSent);
    };
  }, [conversation.id, scrollToBottom]);

  useEffect(() => {
    if (queuedItems.length > 0) scrollToBottom(true);
  }, [queuedItems.length, scrollToBottom]);

  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      {/* Utility toolbar */}
      <div className="border-b bg-background/95 px-3 py-1.5 flex items-center gap-1.5">
        {searchOpen ? (
          <div className="flex-1 flex items-center gap-1.5">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Input
              data-testid="thread-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  navMatch(e.shiftKey ? 1 : -1);
                } else if (e.key === "Escape") {
                  closeSearch();
                }
              }}
              placeholder={t("search_in_conversation")}
              className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 px-1"
              autoFocus
            />
            {searchActive && (
              <span
                data-testid="thread-search-counter"
                className={`text-xs whitespace-nowrap px-1 shrink-0 ${searchMatches.length ? "text-muted-foreground" : "text-destructive/80"}`}
              >
                {searchMatches.length ? `${matchIdx + 1}/${searchMatches.length}` : t("search_no_results")}
              </span>
            )}
            <button
              data-testid="thread-search-prev"
              onClick={() => navMatch(-1)}
              disabled={!searchMatches.length || matchIdx <= 0}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30 shrink-0"
              aria-label="Previous match"
            >
              <ChevronUp className="h-4 w-4" />
            </button>
            <button
              data-testid="thread-search-next"
              onClick={() => navMatch(1)}
              disabled={!searchMatches.length || matchIdx >= searchMatches.length - 1}
              className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30 shrink-0"
              aria-label="Next match"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <button data-testid="thread-search-close" onClick={closeSearch} className="h-7 w-7 flex items-center justify-center rounded hover:bg-muted shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1" />
            <button data-testid="thread-catchup-button" onClick={() => setCatchupOpen(true)} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg hover:bg-muted text-muted-foreground text-xs font-medium" title={t("catchup_title")}>
              <Sparkles className="h-4 w-4" />
            </button>
            <button data-testid="thread-starred-button" onClick={openStarred} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg hover:bg-muted text-muted-foreground text-xs font-medium">
              <Star className="h-4 w-4" />
            </button>
            <button data-testid="thread-search-button" onClick={() => setSearchOpen(true)} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg hover:bg-muted text-muted-foreground text-xs font-medium">
              <Search className="h-4 w-4" />
            </button>
            {conversation.type !== "job" && (
              <button data-testid="thread-shared-jobs-button" onClick={openSharedJobs} className="h-8 px-2.5 flex items-center gap-1.5 rounded-lg hover:bg-muted text-muted-foreground text-xs font-medium">
                <Briefcase className="h-4 w-4" /> <span className="hidden sm:inline">{t("shared_jobs")}</span>
              </button>
            )}
          </>
        )}
      </div>

      {/* "O que perdi?" — offered automatically when a lot happened while away */}
      {missedCount >= 10 && !catchupDismissed && (
        <div className="px-4 py-1.5 bg-primary/10 border-b flex items-center gap-2 text-xs" data-testid="catchup-bar">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
          <button
            data-testid="catchup-bar-open"
            onClick={() => setCatchupOpen(true)}
            className="font-semibold text-primary underline underline-offset-2 truncate"
          >
            {t("catchup_chip")}
          </button>
          <span className="text-muted-foreground truncate">· {missedCount}</span>
          <button data-testid="catchup-bar-dismiss" onClick={() => setCatchupDismissed(true)} className="ml-auto h-6 w-6 flex items-center justify-center rounded hover:bg-background shrink-0">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Job filter bar */}
      {jobFilter && (
        <div className="px-4 py-1.5 bg-primary/10 border-b flex items-center gap-2 text-xs" data-testid="job-filter-bar">
          <Briefcase className="h-3.5 w-3.5 text-primary" />
          <span>{t("filtering_by")} <span className="font-mono-plex font-semibold text-primary">#{jobFilter.job_number}</span> · {jobFilter.customer_name}</span>
          <button data-testid="job-filter-clear" onClick={() => setJobFilter(null)} className="ml-auto h-6 w-6 flex items-center justify-center rounded hover:bg-background">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Context bar: working on suggestion */}
      {showContextBar && (
        <div className="px-4 py-2 bg-accent/15 border-b" data-testid="context-bar">
          <div className="flex items-center gap-2 text-xs">
            <Hammer className="h-3.5 w-3.5 text-accent-foreground shrink-0" />
            <span className="truncate">
              {t("working_on")} <span className="font-mono-plex font-semibold text-primary">#{suggested.job_number}</span> {suggested.customer_name}
            </span>
            <div className="ml-auto flex items-center gap-1.5 shrink-0">
              <span className="text-muted-foreground hidden sm:inline">{t("link_msgs_prompt")} #{suggested.job_number}?</span>
              <Button size="sm" variant="default" data-testid="context-bar-yes" className="h-7 text-xs px-2.5" onClick={() => setLinkedJob(suggested)}>
                {t("yes")}
              </Button>
              <Button size="sm" variant="ghost" data-testid="context-bar-dismiss" className="h-7 text-xs px-2" onClick={() => setSuggestDismissed(true)}>
                {t("not_now")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pinned message bar — success tint so it never blends with the amber job suggestion bar */}
      {pins.length > 0 && (
        <div
          className="px-3 py-2 bg-[hsl(var(--success))]/10 border-b border-l-2 border-l-[hsl(var(--success))] flex items-center gap-2 text-xs cursor-pointer hover:bg-[hsl(var(--success))]/20 transition-colors"
          data-testid="pinned-bar"
          onClick={() => jumpToMessage(pins[0].message_id)}
        >
          <Pin className="h-3.5 w-3.5 text-[hsl(var(--success))] shrink-0 rotate-45" />
          <div className="min-w-0 flex-1 truncate">
            <span className="font-semibold">{pins[0].message?.sender_name}: </span>
            <span>{msgPreview(pins[0].message || {})}</span>
            <span className="text-muted-foreground ml-1.5 hidden sm:inline">· {t("pinned_by")} {pins[0].pinned_by_name}</span>
          </div>
          {pins.length > 1 && (
            <span className="text-[10px] font-bold bg-background rounded-full px-1.5 py-0.5 shrink-0" data-testid="pinned-more-count">+{pins.length - 1}</span>
          )}
          {canUnpin(pins[0]) && (
            <button
              data-testid="unpin-button"
              onClick={(e) => { e.stopPropagation(); unpin(pins[0]); }}
              className="h-6 w-6 flex items-center justify-center rounded hover:bg-background shrink-0"
              aria-label="Unpin"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div ref={scrollRef} onClick={dismissKeyboardOnTap} onTouchStart={dismissKeyboardOnTap} className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-5 py-3 chat-wallpaper" style={{ "--chat-bg-url": `url('${user?.conv_wallpapers?.[conversation.id] ? mediaSrcById(user.conv_wallpapers[conversation.id]) : (user?.wallpaper_media_id ? mediaSrcById(user.wallpaper_media_id) : "/chat-bg.jpg")}')` }} data-testid="thread-messages">
        <div className="max-w-3xl mx-auto">
          {loading && (
            <div className="space-y-3 py-4">
              {[1, 2, 3].map((i) => <div key={i} className={`h-14 bg-muted rounded-2xl animate-pulse w-2/3 ${i % 2 ? "" : "ml-auto"}`} />)}
            </div>
          )}
          {!loading && displayed.length === 0 && (
            <div className="text-center text-sm text-muted-foreground py-16">{t("no_messages")}</div>
          )}
          {/* one wrapper per calendar day: the sticky date pill is scoped to its day, so
              pills never pile up at the top (WhatsApp behaviour) */}
          {dayGroups.map((g) => (
          <div key={g.key} className="relative">
          {g.items.map((item) =>
            item.kind === "day" ? (
              <div key={item.id} className="flex justify-center my-3 sticky top-1 z-10" data-testid="day-separator">
                <span className="text-[11px] font-medium tracking-wide text-muted-foreground bg-card/95 backdrop-blur border shadow-sm rounded-full px-3 py-1">
                  {item.label}
                </span>
              </div>
            ) : item.kind === "unread" ? (
              <div key={item.id} id="unread-divider" className="flex items-center gap-3 my-3" data-testid="unread-divider">
                <span className="flex-1 h-px bg-primary/30" />
                <span className="text-[11px] font-semibold text-primary bg-primary/10 rounded-full px-3 py-1">
                  {item.count === 1 ? t("unread_divider_one") : t("unread_divider_many").replace("{n}", item.count)}
                </span>
                <span className="flex-1 h-px bg-primary/30" />
              </div>
            ) : item.kind === "mirror" ? (
              <MirroredBubble
                key={item.id}
                message={item.message}
                sender={usersMapRef.current[item.message.sender_id]}
                own={item.message.sender_id === user.id}
              />
            ) : item.kind === "group" ? (
              <MediaGroupBubble
                key={item.id}
                messages={item.messages}
                sender={usersMapRef.current[item.messages[0].sender_id]}
                own={item.messages[0].sender_id === user.id}
                conversation={conversation}
                onOpenPhoto={(media, message) => setViewer({ media, message })}
                onOpenVideo={(media, message) => setVideoViewer({ media, message })}
                focusedId={focusedId}
                onReply={replyToMessage}
                onReact={reactToMessage}
                starredIds={starredIds}
                onToggleStar={toggleStar}
                onPin={(m) => setPinTarget(m)}
              />
            ) : (
            <div
              key={item.id}
              id={`msg-${item.message.id}`}
              className={`rounded-2xl transition-colors duration-300 ${focusedId === item.message.id ? "bg-accent/25 ring-2 ring-accent/60 px-1" : ""}`}
            >
              <MessageBubble
                message={item.message}
                sender={usersMapRef.current[item.message.sender_id]}
                conversation={conversation}
                highlightQuery={searchActive ? searchQuery.trim() : ""}
                selectMode={selectMode}
                selected={selectedIds.includes(item.message.id)}
                onToggleSelect={toggleSelect}
                onStartSelect={startSelect}
                onCreateIssue={createIssueFrom}
                onCreateTask={createTaskFrom}
                onShareWithJob={shareWithJob}
                onUnlink={(m) => unlinkMessages([m.id])}
                onOpenPhoto={(media, message) => setViewer({ media, message })}
                onOpenVideo={(media, message) => setVideoViewer({ media, message })}
                onToggleDecision={toggleDecision}
                onIssueStatus={changeIssueStatus}
                onResolveIssue={(m) => setResolveFor(m)}
                onReact={reactToMessage}
                onReply={replyToMessage}
                onEdit={(m) => (m.type === "measurement" ? (m.meta?.catalog ? setCatalogPick(m) : setEditingMeasure(m)) : setEditingMessage(m))}
                onQuoteJump={jumpToMessage}
                onSaveToFiles={onSaveToFiles}
                onDelete={(m) => setDeleteMsg(m)}
                reads={reads}
                delivers={delivers}
                isStarred={starredIds.has(item.message.id)}
                onToggleStar={toggleStar}
                onPin={(m) => setPinTarget(m)}
              />
            </div>
            )
          )}
          </div>
          ))}
          {/* Offline queue: pending/failed outgoing messages */}
          {queuedItems.map((qi) => (
            <PendingBubble key={qi.client_id} item={qi} t={t} />
          ))}
          {/* Someone else is typing / recording a voice note */}
          {presence.map((p) => (
            <div key={p.user_id} data-testid={`presence-bubble-${p.kind}`} className="flex justify-start mt-1.5 animate-in fade-in slide-in-from-bottom-1 duration-200">
              <div className="rounded-2xl rounded-bl-md bg-card border shadow-sm px-3 py-2 flex items-center gap-2 text-xs text-muted-foreground">
                {conversation.type !== "dm" && <span className="font-medium text-foreground">{(p.name || "").split(" ")[0]}</span>}
                {p.kind === "recording" ? (
                  <>
                    <Mic className="h-3.5 w-3.5 text-destructive presence-mic" />
                    <span>{t("presence_recording_label")}</span>
                  </>
                ) : (
                  <span className="presence-dots" aria-label={t("presence_typing_label")}><i /><i /><i /></span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating jump-to-latest button (appears when scrolled up) */}
      {(showJump || newCount > 0) && !selectMode && (
        <button
          data-testid="jump-to-bottom-button"
          onClick={() => { stickBottomRef.current = true; setShowJump(false); setNewCount(0); scrollToBottom(true); }}
          className={`absolute bottom-24 right-4 z-30 h-11 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center gap-1.5 hover:opacity-90 active:scale-95 transition-transform ${newCount > 0 ? "px-4 animate-in fade-in slide-in-from-bottom-2" : "w-11"}`}
          aria-label="Jump to latest messages"
        >
          <ChevronDown className="h-5 w-5" />
          {newCount > 0 && (
            <span className="text-sm font-semibold" data-testid="jump-new-count">
              {t("new_messages_pill").replace("{n}", newCount)}
            </span>
          )}
        </button>
      )}

      {/* Selection bar */}
      {selectMode && (
        <div className="border-t bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap" data-testid="selection-bar">
          <span className="text-sm font-medium shrink-0">{selectedIds.length} {t("selected")}</span>
          <Button size="sm" data-testid="link-selected-button" disabled={selectedIds.length === 0} onClick={() => setPicker({ mode: "selection" })} className="h-8 px-2.5">
            <Link2 className="h-4 w-4 mr-1" /> {t("link_selected")}
          </Button>
          {selectedLinked.length > 0 && (
            <Button size="sm" variant="outline" data-testid="unlink-selected-button" onClick={async () => { await unlinkMessages(selectedLinked); exitSelect(); }} className="h-8 px-2.5">
              <Unlink className="h-4 w-4 mr-1" /> {t("unlink_selected")} ({selectedLinked.length})
            </Button>
          )}
          <Button size="sm" variant="ghost" data-testid="selection-cancel" onClick={exitSelect} className="ml-auto h-8 px-2" aria-label={t("cancel")}>
            <X className="h-4 w-4" /><span className="hidden sm:inline ml-1">{t("cancel")}</span>
          </Button>
        </div>
      )}

      {!selectMode && (
        <Composer
          conversation={conversation}
          replyContext={replyContext}
          clearReply={() => setReplyContext(null)}
          editingMessage={editingMessage}
          clearEdit={() => setEditingMessage(null)}
          onEdited={(updated) => {
            setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
            setEditingMessage(null);
          }}
          linkedJob={linkedJob}
          setLinkedJob={setLinkedJob}
          linkVisibility={linkVisibility}
          setLinkVisibility={setLinkVisibility}
        />
      )}

      {catalogPick && (
        <CatalogEditPicker
          message={catalogPick}
          onClose={() => setCatalogPick(null)}
          onPick={(i) => { setEditingMeasure({ ...catalogPick, catalogIndex: i }); setCatalogPick(null); }}
        />
      )}
      <MeasurementDialog
        open={!!editingMeasure}
        editing={editingMeasure}
        conversation={conversation}
        onClose={() => setEditingMeasure(null)}
        onEdited={(updated) => setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)))}
      />
      {resolveFor && (
        <ResolveIssueDialog
          open={!!resolveFor}
          message={resolveFor}
          conversation={conversation}
          onClose={() => setResolveFor(null)}
          onResolved={(issueId) => {
            // Optimistic: update the issue bubble status right away
            setMessages((prev) => prev.map((m) => (
              m.meta?.issue_id === issueId ? { ...m, meta: { ...m.meta, status: "resolved" } } : m
            )));
          }}
        />
      )}

      {viewer && (() => {
        // Gallery of all image media in the (currently displayed) conversation, in order
        const gallery = displayed.filter((m) => !m.mirrored).flatMap((m) =>
          (m.media || [])
            .filter((md) => md.kind === "image")
            .map((md) => ({ media: md, message: m })));
        const idx = gallery.findIndex((g) => g.media.id === viewer.media.id);
        return (
          <PhotoViewer
            key={viewer.media.id}
            open={!!viewer}
            media={viewer.media}
            message={viewer.message}
            conversation={conversation}
            onClose={() => setViewer(null)}
            onPrev={idx > 0 ? () => setViewer(gallery[idx - 1]) : undefined}
            onNext={idx >= 0 && idx < gallery.length - 1 ? () => setViewer(gallery[idx + 1]) : undefined}
            gallery={gallery}
            onSelectItem={(g) => setViewer(g)}
            onReplyToArea={(ctx) => {
              // Keep the quote/shortcut back to the ORIGINAL photo message
              const m = viewer.message;
              setReplyContext({
                ...ctx,
                quote: m ? {
                  message_id: m.id,
                  sender_id: m.sender_id,
                  sender_name: usersMapRef.current[m.sender_id]?.name || "",
                  content: ((m.meta?.title || m.content || "")).slice(0, 140),
                  type: m.type,
                  media_id: ctx.media_id || (m.media_ids || [])[0] || null,
                } : undefined,
              });
              setViewer(null);
            }}
          />
        );
      })()}

      {/* Video viewer: fullscreen player with pause + frame markup (annotations reply to the video) */}
      {videoViewer && (
        <VideoViewer
          open={!!videoViewer}
          media={videoViewer.media}
          message={videoViewer.message}
          conversation={conversation}
          markups={messages
            .filter((x) => x.meta?.video_markup && x.meta?.video_media_id === videoViewer.media.id && typeof x.meta?.video_time === "number")
            .map((x) => ({ time: x.meta.video_time, id: x.id }))}
          onClose={() => setVideoViewer(null)}
        />
      )}

      {/* Shared Jobs dialog */}
      <Dialog open={sharedJobsOpen} onOpenChange={(o) => !o && setSharedJobsOpen(false)}>
        <DialogContent className="max-w-md" data-testid="shared-jobs-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-primary" /> {t("shared_jobs")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            {sharedJobs.length === 0 && <div className="text-center text-sm text-muted-foreground py-8">{t("no_linked")}</div>}
            {sharedJobs.map((sj) => (
              <div
                key={sj.job.id}
                data-testid={`shared-job-row-${sj.job.job_number}`}
                onClick={() => {
                  // Re-enter the job context: filter the view AND activate the job
                  // topic so everything written is linked until the user taps the X
                  setJobFilter(sj.job);
                  setLinkVisibilityState("shared");
                  setLinkedJob(sj.job, "shared");
                  setSharedJobsOpen(false);
                }}
                className="px-3 py-2.5 rounded-lg hover:bg-muted/60 cursor-pointer transition-colors flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono-plex text-xs font-semibold text-primary">#{sj.job.job_number}</span>
                    <span className="text-sm font-medium truncate">{sj.job.customer_name}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {sj.message_count} {t("msgs")} · {sj.photo_count} {t("photos").toLowerCase()}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Job picker (selection link / issue / task without job context) */}
      <JobPickerDialog open={!!picker} onClose={() => setPicker(null)} onPick={onPickJob} />
      <CatchUpDialog
        open={catchupOpen}
        onClose={() => setCatchupOpen(false)}
        conversationId={conversation.id}
        since={catchupSinceRef.current}
      />

      {/* Create Issue from message */}
      {issueFrom && (
        <IssueDialog
          open={!!issueFrom}
          onClose={() => setIssueFrom(null)}
          conversation={conversation}
          jobId={issueFrom.jobId}
          prefill={{
            description: issueFrom.message.content,
            mediaIds: issueFrom.message.media_ids || [],
            reportedBy: issueFrom.message.sender_id,
            reportedByName: usersMapRef.current[issueFrom.message.sender_id]?.name,
          }}
        />
      )}

      {/* Create Task from message */}
      {taskFrom && (
        <TaskFromMessageDialog
          open={!!taskFrom}
          onClose={() => setTaskFrom(null)}
          jobId={taskFrom.jobId}
          conversationId={conversation.id}
          participants={conversation.participants || []}
          message={taskFrom.message}
        />
      )}

      {/* Delete message: for me (always) / for everyone (own recent or admin) */}
      <AlertDialog open={!!deleteMsg} onOpenChange={(o) => !o && setDeleteMsg(null)}>
        <AlertDialogContent data-testid="delete-message-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_message")}</AlertDialogTitle>
            <AlertDialogDescription className="line-clamp-3">
              {t("delete_message_confirm")}
              {deleteMsg?.content ? ` — "${deleteMsg.content.slice(0, 120)}"` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-col gap-2">
            <AlertDialogAction data-testid="delete-message-for-me" className="w-full" onClick={hideMessageForMe}>
              {t("delete_for_me")}
            </AlertDialogAction>
            {canDeleteForAll(deleteMsg) && (
              <AlertDialogAction data-testid="delete-message-confirm" className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={confirmDeleteMessage}>
                {t("delete_for_everyone")}
              </AlertDialogAction>
            )}
            <AlertDialogCancel data-testid="delete-message-cancel" className="w-full mt-0">{t("cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pin duration picker (24h / 7d / 30d) */}
      <Dialog open={!!pinTarget} onOpenChange={(v) => { if (!v) setPinTarget(null); }}>
        <DialogContent className="max-w-xs" data-testid="pin-duration-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pin className="h-4 w-4 rotate-45" /> {t("pin_for")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-2">
            <Button variant="outline" data-testid="pin-24h" onClick={() => pinFor(24)}>{t("hours_24")}</Button>
            <Button variant="outline" data-testid="pin-7d" onClick={() => pinFor(168)}>{t("days_7")}</Button>
            <Button variant="outline" data-testid="pin-30d" onClick={() => pinFor(720)}>{t("days_30")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Starred (favorites) list */}
      <Dialog open={starredOpen} onOpenChange={setStarredOpen}>
        <DialogContent className="max-w-md" data-testid="starred-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-4 w-4 text-amber-500 fill-amber-400" /> {t("favorites")}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto -mx-2 px-2">
            {starredList === null && (
              <div className="space-y-2 py-2">
                <div className="h-12 rounded-lg bg-muted animate-pulse" />
                <div className="h-12 rounded-lg bg-muted animate-pulse" />
              </div>
            )}
            {starredList !== null && starredList.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-8">{t("no_favorites")}</p>
            )}
            {(starredList || []).map((m) => (
              <button
                key={m.id}
                data-testid={`starred-row-${m.id}`}
                onClick={() => { setStarredOpen(false); setTimeout(() => jumpToMessage(m.id), 150); }}
                className="w-full text-left px-2 py-2.5 rounded-lg hover:bg-muted transition-colors border-b last:border-b-0"
              >
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-0.5">
                  {m.type === "photo" && <Camera className="h-3 w-3" />}
                  {m.type === "video" && <VideoIcon className="h-3 w-3" />}
                  {m.type === "document" && <FileText className="h-3 w-3" />}
                  {m.type === "voice" && <Mic className="h-3 w-3" />}
                  <span className="font-semibold">{m.sender_name}</span>
                  <span>· {fmtTime(m.created_at)}</span>
                </div>
                <div className="text-sm truncate">{msgPreview(m)}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---- Offline queue pending bubble ----
function PendingBubble({ item, t }) {
  const isPhoto = item.kind === "photo";
  const thumb = isPhoto ? (item.payload?.editedDataUrl || item.payload?.originalDataUrl) : null;
  const text = isPhoto ? (item.payload?.caption || "") : (item.payload?.content || item.preview || "");
  const failed = item.status === "failed";
  const sending = item.status === "sending";

  return (
    <div className="flex justify-end mb-2" data-testid="pending-message-bubble">
      <div className={`max-w-[78%] sm:max-w-[65%] rounded-2xl rounded-br-md border px-3 py-2 ${failed ? "border-destructive/40 bg-destructive/5" : "border-dashed border-muted-foreground/30 bg-muted/40"}`}>
        {thumb && (
          <img src={thumb} alt="" className="rounded-lg max-h-44 w-auto object-cover mb-1.5 opacity-80" />
        )}
        {text && <div className="text-sm whitespace-pre-wrap break-words opacity-80">{text}</div>}
        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground">
          {failed ? (
            <>
              <CloudOff className="h-3 w-3 text-destructive" />
              <span className="text-destructive font-medium">{t("msg_failed")}</span>
              <button
                data-testid="pending-retry-button"
                onClick={() => retryQueued(item.client_id)}
                className="ml-1 inline-flex items-center gap-0.5 font-medium text-primary hover:underline"
              >
                <RefreshCw className="h-3 w-3" /> {t("retry")}
              </button>
              <button
                data-testid="pending-discard-button"
                onClick={() => removeQueued(item.client_id)}
                className="ml-1 inline-flex items-center gap-0.5 font-medium text-muted-foreground hover:underline"
              >
                <Trash2 className="h-3 w-3" /> {t("discard")}
              </button>
            </>
          ) : (
            <>
              <Clock className={`h-3 w-3 ${sending ? "animate-spin" : ""}`} />
              <span>{sending ? t("msg_sending") : t("msg_pending")}</span>
              <span className="opacity-60">· {fmtTime(item.created_at)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
